// src/features/workbooks/new.tsx

import { Form } from "@/components/ui/form";
import { useEditForm } from "@/features/Edit/hooks";
import { FormActionsNew } from "@/features/FormCard/Actions";
import {
  AutocompleteAsyncField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";
import { NewDiff } from "@/features/FormCard/NewDiff";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import {
  CLIENT_GROUP_OPTIONS,
  CLIENT_GROUP_VALUES,
} from "@/features/Workspaces/clientGroups";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

// Function to adjust AutocompleteAsyncField data for submission
const adjustDataForSubmission = (data: any) => {
  const adjustedData = { ...data };

  // Handle group_user_name AutocompleteAsyncField
  if (data.group_user_name && typeof data.group_user_name === "object") {
    // Extract the ID from the autocomplete object
    adjustedData.user_group_id = data.group_user_name.id || data.user_group_id;
  } else if (data.group_user_name == null) {
    adjustedData.user_group_id = null;
  }
  delete adjustedData.group_user_name;

  return adjustedData;
};

const client = "tableau-quantilia";
const table_name = "workbooks";

const FormSchema = z.object({
  client: z.string().nullable().optional(),
  workspace_group: z.enum(CLIENT_GROUP_VALUES).nullable().optional(),
  name: z.string().min(1, "Name is required"),
  menu: z.string().min(1, "Menu is required"),
  menu_quantilia: z.string().min(1, "Menu Quantilia is required"),
  sort_order: z.coerce.number().optional().nullable(),
  uuid: z.string().min(1, "UUID is required"),
  group_user_name: z
    .union([
      z.string(),
      z.object({
        id: z.string(),
        name: z.string(),
        text: z.string(),
      }),
    ])
    .optional(),
  user_group_id: z.string().nullable().optional(),
});

export function WorkbooksNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
    disable_translations_request: true,
    adjustData: adjustDataForSubmission,
  });

  const navigateToList = () => navigate(-1);

  const DEFAULTS = {
    name: "",
    uuid: "",
    menu: "",
    menu_quantilia: "",
    client: "",
    workspace_group: null,
    sort_order: null,
    group_user_name: null,
    user_group_id: null,
  };

  const onResetNew = () => {
    form.reset(DEFAULTS, {
      keepDirty: false,
      keepTouched: false,
      keepErrors: false,
      keepValues: false,
    });
    handleReset();
  };

  return (
    <div className="grid place-items-center h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                New Workbook
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1: Client & Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="name"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Name",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="uuid"
                    label={t(`${table_name}.columns.uuid`, {
                      ns: "tables",
                      defaultValue: "UUID",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>
                {/* Row 2: Menu & Menu Quantilia */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="menu"
                    label={t(`${table_name}.columns.menu`, {
                      ns: "tables",
                      defaultValue: "Menu",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="menu_quantilia"
                    label={t(`${table_name}.columns.menu_quantilia`, {
                      ns: "tables",
                      defaultValue: "Menu Quantilia",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>
                {/* Row 3: Workspace Group & Client */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="workspace_group"
                    label={t(`${table_name}.columns.workspace_group`, {
                      ns: "tables",
                      defaultValue: "Workspace Group",
                    })}
                    options={CLIENT_GROUP_OPTIONS}
                    allow_empty_option
                    control={form.control}
                  />
                  <InputField
                    name="client"
                    label={t(`${table_name}.columns.client`, {
                      ns: "tables",
                      defaultValue: "Client",
                    })}
                    control={form.control}
                  />
                </div>
                {/* Row 4: Sort Order */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="sort_order"
                    label={t(`${table_name}.columns.sort_order`, {
                      ns: "tables",
                      defaultValue: "Sort Order",
                    })}
                    type="number"
                    control={form.control}
                    allowZero
                  />
                </div>
                {/* Row 5: User Group */}
                <AutocompleteAsyncField
                  name="group_user_name"
                  label={t(`${table_name}.columns.group_user_name`, {
                    ns: "tables",
                    defaultValue: "User Group",
                  })}
                  base_url="tableau-quantilia/workbooks"
                  column_name="group_user_name"
                  control={form.control}
                />
              </CardBody>
              <CardFooter>
                <FormActionsNew
                  onReset={onResetNew}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_create")}
          >
            {changedFields ? NewDiff(changedFields, []) : "No changes detected"}
          </ConfirmationModal>

          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
